import {
  PRODUCT_TYPE_STOCK_MODE,
  IProductTypeStockSettings,
} from '../models/i-product-type';

import { getPositiveNumber, isNumber } from './number-helper';

interface IProductTypeQuantitySettings {
  minQuantity?: number;
  maxQuantity?: number;
  minQuantityPerProduct?: number;
  increment?: number;
  stockSettings?: IProductTypeStockSettings;
}

export function isStockSettingsEnabled(productType: IProductTypeQuantitySettings): boolean {
  const stockSettings = productType.stockSettings;

  return Boolean(stockSettings && stockSettings.isEnabled);
}

export function isCountDownStockSettingsEnabled(productType: IProductTypeQuantitySettings): boolean {
  const stockSettings = productType.stockSettings;

  return Boolean(
    isStockSettingsEnabled(productType) &&
    stockSettings.mode === PRODUCT_TYPE_STOCK_MODE.COUNT_DOWN &&
    isNumber(stockSettings.remainingInStock)
  );
}

export function isDailyLimitStockSettingsEnabled(productType: IProductTypeQuantitySettings): boolean {
  const stockSettings = productType.stockSettings;

  return Boolean(
    isStockSettingsEnabled(productType) &&
    stockSettings.mode === PRODUCT_TYPE_STOCK_MODE.DAILY_LIMIT &&
    isNumber(stockSettings.dailyLimit)
  );
}

export function hasDailyLimitSoldOutDates(productType: IProductTypeQuantitySettings): boolean {
  const stockSettings = productType.stockSettings;

  return Boolean(
    isDailyLimitStockSettingsEnabled(productType) &&
    stockSettings.soldOutDates &&
    stockSettings.soldOutDates.length
  );
}

export function getStockQuantity(productType: IProductTypeQuantitySettings): number | null {
  if (!isCountDownStockSettingsEnabled(productType)) {
    return null;
  }

  return Math.max(productType.stockSettings.remainingInStock, 0);
}

export function getDailyLimitQuantity(productType: IProductTypeQuantitySettings): number | null {
  if (!isDailyLimitStockSettingsEnabled(productType)) {
    return null;
  }

  return Math.max(productType.stockSettings.dailyLimit, 0);
}

export function getStockQuantityLimit(productType: IProductTypeQuantitySettings): number | null {
  if (isCountDownStockSettingsEnabled(productType)) {
    return Math.max(productType.stockSettings.remainingInStock, 0);
  }

  if (isDailyLimitStockSettingsEnabled(productType)) {
    return getDailyLimitQuantity(productType);
  }

  return null;
}

export function getEffectiveMaxQuantity(productType: IProductTypeQuantitySettings): number | null {
  const maxQuantity = getPositiveNumber(productType.maxQuantity);
  const stockQuantityLimit = getStockQuantityLimit(productType);

  const isStockLimitValidNumber = isNumber(stockQuantityLimit);

  if (isNumber(maxQuantity) && isStockLimitValidNumber) {
    return Math.min(maxQuantity, stockQuantityLimit);
  }

  if (isStockLimitValidNumber) {
    return stockQuantityLimit;
  }

  return maxQuantity;
}

export function getMaxQuantityForLineInGroup(
  productType: IProductTypeQuantitySettings,
  currentLineCount: number,
  groupTotalCount: number
): number | null {
  const maxQuantity = getEffectiveMaxQuantity(productType);

  return (maxQuantity !== null)
    ? Math.max(0, maxQuantity - groupTotalCount + currentLineCount)
    : null;
}

export function getAvailableQuantityToAdd(
  productType: IProductTypeQuantitySettings,
  currentQuantity: number = 0
): number | null {
  const maxQuantity = getEffectiveMaxQuantity(productType);

  if (!isNumber(maxQuantity)) {
    return null;
  }

  return Math.max(maxQuantity - currentQuantity, 0);
}

/**
 * What can still be added to one build (orderLineBuilderId) of a product when the cart may hold several builds of it:
 * maxQuantity limits each build, the stock limit the whole cart. With a single build both quantities are the same and
 * this equals getAvailableQuantityToAdd.
 */
export function getAvailableQuantityToAddToBuild(
  productType: IProductTypeQuantitySettings,
  buildQuantity: number,
  cartQuantity: number
): number | null {
  const maxQuantity = getPositiveNumber(productType.maxQuantity);
  const stockQuantityLimit = getStockQuantityLimit(productType);

  const limits: number[] = [];
  if (isNumber(maxQuantity)) {
    limits.push(maxQuantity - buildQuantity);
  }
  if (isNumber(stockQuantityLimit)) {
    limits.push(stockQuantityLimit - cartQuantity);
  }

  return limits.length ? Math.max(Math.min(...limits), 0) : null;
}

export function canAddQuantity(
  productType: IProductTypeQuantitySettings,
  currentQuantity: number,
  quantityToAdd: number
): boolean {
  const availableQuantity = getAvailableQuantityToAdd(productType, currentQuantity);

  return !isNumber(availableQuantity) || availableQuantity >= quantityToAdd;
}
